import React, { useState } from "react";
import { DateTime } from "luxon";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";

import { CardContainer, Container } from "../../../components/Container";
import { withAppConfig } from "../../../components/AppConfigContext";
import { withInstallation } from "../../../components/InstallationContext";
import authManager from "../../../lib/authManager";
import * as enums from "../../../lib/enums";
import TransactionList from "./TransactionList";

const END_OF_DAY_MINUTES = 23 * 60 + 59;
const END_TIME_OPTIONS = [
  ...enums.ENTRY_EXIT_24HR_OPTIONS,
  { value: END_OF_DAY_MINUTES, label: "23:59" },
];

const useStyles = makeStyles({
  filters: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
  },
  date: { minWidth: 180 },
  time: { minWidth: 112 },
  installation: { minWidth: 180 },
  selectButton: {
    height: 40,
    textTransform: "none",
    background: "#3127B8",
    color: "#fff",
    "&:hover": { background: "#241C8A" },
  },
});

export function combineDateAndTime(date, minutesPastMidnight) {
  if (!date) return null;
  return DateTime.fromISO(date)
    .startOf("day")
    .plus({ minutes: Number(minutesPastMidnight || 0) })
    .toFormat("yyyy-MM-dd HH:mm");
}

function Transactions({ installation }) {
  const classes = useStyles();
  const isRootAdmin = Boolean(authManager.isRootAdmin());
  const installationOptions = isRootAdmin
    ? enums.INSTALLATION_TRANSACTIONS_SEARCH_OPTIONS
    : enums.INSTALLATION_TRANSACTIONS_SEARCH_OPTIONS.filter((option) => option.value === "current");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(END_OF_DAY_MINUTES);
  const [installationsToQuery, setInstallationToQuery] = useState(isRootAdmin ? "all" : "current");
  const [selectedRange, setSelectedRange] = useState(null);

  const handleSelect = () => {
    if (!startDate || !endDate) {
      alert("Please select a start and end date.");
      return;
    }
    const start = combineDateAndTime(startDate, startTime);
    const end = combineDateAndTime(endDate, endTime);
    if (DateTime.fromFormat(start, "yyyy-MM-dd HH:mm") >= DateTime.fromFormat(end, "yyyy-MM-dd HH:mm")) {
      alert("End date/time must be later than start date/time.");
      return;
    }
    setSelectedRange({ startDate: start, endDate: end });
  };

  const currentInstallation = installationsToQuery === "current";
  return (
    <CardContainer>
      <Container size="large" spaceBelow="medium">
        <p>
          Select a date/time range. The range is interpreted in {currentInstallation
            ? `${installation.timeZoneName} (${DateTime.now().setZone(installation.timeZoneName).toFormat("ZZZZ")})`
            : "UTC"}.
        </p>
        <Box className={classes.filters}>
          <TextField
            className={classes.date}
            size="small"
            variant="outlined"
            type="date"
            label="Start date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ placeholder: "Enter start date" }}
          />
          <TextField className={classes.time} size="small" variant="outlined" select label="Start time" value={startTime} onChange={(event) => setStartTime(event.target.value)}>
            {enums.ENTRY_EXIT_24HR_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </TextField>
          <TextField
            className={classes.date}
            size="small"
            variant="outlined"
            type="date"
            label="End date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ placeholder: "Enter end date" }}
          />
          <TextField className={classes.time} size="small" variant="outlined" select label="End time" value={endTime} onChange={(event) => setEndTime(event.target.value)}>
            {END_TIME_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </TextField>
          <TextField className={classes.installation} size="small" variant="outlined" select label="Installation" value={installationsToQuery} onChange={(event) => setInstallationToQuery(event.target.value)}>
            {installationOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </TextField>
          <Button className={classes.selectButton} variant="contained" onClick={handleSelect}>Select Charges</Button>
        </Box>
        {selectedRange && (
          <TransactionList
            installation={currentInstallation ? installation : null}
            installationId={currentInstallation ? installation.id : null}
            startDate={selectedRange.startDate}
            endDate={selectedRange.endDate}
            displayZone={currentInstallation ? installation.timeZoneName : "utc"}
          />
        )}
      </Container>
    </CardContainer>
  );
}

export default withInstallation(withAppConfig(Transactions));
